import { useQuery } from '@apollo/client/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { GQL_APP_CONFIG } from '~/gql/app-config/app-config'
import { isPlatformPublicPath } from '~/routes/orgRouting'
import AuthLayout from './components/AuthLayout'
import SignupChoice from './signup/SignupChoice'
import SignupForm from './signup/SignupForm'
import SignupLearner from './signup/SignupLearner'
import SignupSent from './signup/SignupSent'

/**
 * Parcours d'inscription qualifié en libre-service (#155), dans le cadre de la
 * connexion (#279).
 *
 * Étapes : choix du profil → (apprenant/formateur = impasse assumée, on n'ouvre
 * pas de compte sans organisme invitant) ou formulaire qualifié → écran « vérifiez
 * votre boîte mail ».
 */
type Step = 'choice' | 'learner' | 'form' | 'sent'

function Signup() {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('choice')
  const [sentTo, setSentTo] = useState('')
  const heading = useRef<HTMLHeadingElement>(null)
  const firstStep = useRef(true)

  // Changer d'étape remplace tout le contenu : le focus suit sur le titre, sinon il se perd (RGAA 12.8).
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false
      return
    }
    heading.current?.focus()
  }, [step])

  /**
   * L'écran se monte dans deux contextes, et la question posée n'est pas la même.
   *
   * À la racine (`/signup`, #37) c'est la page d'acquisition de la plateforme :
   * elle n'appartient à aucune organisation et ne consulte donc aucun palier.
   * Elle a longtemps été servie via `/o/default/signup`, ce qui la faisait
   * dépendre du palier de l'org par défaut — laquelle est un vrai client
   * historique : la passer au payant aurait fermé le CTA de la landing sans que
   * rien ne le signale.
   *
   * Sous un basename d'org (`/o/<slug>/signup`) la route reste atteignable en
   * direct même une fois le lien retiré de l'écran de connexion (#216) : on la
   * referme ici plutôt que de compter sur une URL non devinable. Ouvrir un compte
   * plateforme n'a pas de sens depuis l'espace d'un client.
   *
   * On interroge la config plutôt que `useBranding()` pour distinguer « pas
   * encore chargé » de « masqué » — le contexte, lui, part masqué par défaut et
   * redirigerait avant même d'avoir la réponse. Une query en échec redirige aussi :
   * mieux vaut renvoyer au login que d'ouvrir le formulaire sans savoir sur
   * quelle org on est.
   */
  const isPlatform = isPlatformPublicPath(window.location.pathname)
  const { data: config, loading: configLoading } = useQuery(GQL_APP_CONFIG, { skip: isPlatform })
  if (!isPlatform) {
    if (configLoading) return null
    if (!config?.appConfig.signupCtaVisible) return <Navigate to="/login" replace />
  }

  return (
    <AuthLayout tagline={t('signup.tagline')} taglineSub={t('signup.tagline.sub')}>
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center">
        <h1 ref={heading} tabIndex={-1} className="mb-6 font-heading text-4xl font-extrabold tracking-tight focus:outline-none">
          {step === 'sent' ? t('signup.sent.title') : t('signup.title')}
        </h1>
        {step === 'choice' && <SignupChoice onOrganization={() => setStep('form')} onLearner={() => setStep('learner')} />}
        {step === 'learner' && <SignupLearner onBack={() => setStep('choice')} />}
        {step === 'form' && (
          <SignupForm
            onBack={() => setStep('choice')}
            onSent={(email) => {
              setSentTo(email)
              setStep('sent')
            }}
          />
        )}
        {step === 'sent' && <SignupSent email={sentTo} />}
      </div>
    </AuthLayout>
  )
}

export default Signup
